import React, { useState } from 'react';
import { Box, Collapse } from '@mui/material';
import { Chip } from '../../../../Chip';
import { DayTimeType } from '../../../../DayTimeAndRange/DayTime/types';
import { SwitchWithLabel } from '../../../../Switch';
import { TextField } from '../../../../TextField';
import { Typography } from '../../../../Typography';
import { formatLastUpdated, getScheduledDayCount, getStatusChipColor } from '../../utils';
import { CleaningHistoryList } from '../CleaningHistoryList';
import { CleaningScheduleGrid } from '../CleaningScheduleGrid';

export interface CleaningTaskCardProps {
  /**
   * Translation function.
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * Whether we are on a small-screen device (forwarded to the schedule edit modal).
   */
  isSmallDevice: boolean;
  /**
   * The task title, e.g. "Cleaning Task A" (from the group-1 label attribute).
   */
  title: string;
  /**
   * Current cleaning status (read-only).
   */
  status?: string;
  /**
   * ISO timestamp of the latest status update (read-only).
   */
  lastUpdated?: string;
  /**
   * Raw newline-delimited history string (read-only).
   */
  history?: string;
  /**
   * The weekly DAYTIME schedule.
   */
  schedule?: DayTimeType[];
  /**
   * Whether this cleaning task is enabled (`cleanRequired`).
   */
  enabled?: boolean;
  /**
   * Label for the enabled toggle. When omitted the toggle is not rendered.
   */
  enabledLabel?: string;
  /**
   * Label for the current-status card.
   */
  statusLabel?: string;
  /**
   * Label for the latest-update card.
   */
  lastUpdatedLabel?: string;
  /**
   * Label for the weekly schedule section.
   */
  scheduleLabel?: string;
  /**
   * Label for the history section.
   */
  historyLabel?: string;
  /**
   * The "missed after" trigger, in minutes (`cleanTrigger`).
   */
  trigger?: number;
  /**
   * Label describing the trigger field.
   */
  triggerLabel?: string;
  /**
   * When true, all controls render read-only.
   */
  readOnly?: boolean;
  /**
   * Whether the card starts expanded.
   * @default true
   */
  defaultExpanded?: boolean;
  /**
   * Called with the updated schedule when the user edits it.
   */
  onScheduleChange: (value: DayTimeType[]) => void;
  /**
   * Called with the new trigger value (minutes) when the user edits it.
   */
  onTriggerChange: (value: number) => void;
  /**
   * Called with the new enabled state when the user toggles it.
   */
  onEnabledChange: (value: boolean) => void;
}

const summaryLabelSx = {
  fontSize: '11px',
  fontWeight: 600,
  color: '#667085',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
} as const;

const sectionHeadingSx = { fontSize: '13px', fontWeight: 600, color: '#182230' } as const;

const summaryCardSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '12px',
  border: '1px solid #EAECF0',
  borderRadius: '8px',
} as const;

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      display: 'inline-flex',
      transition: 'transform .15s',
      transform: open ? 'rotate(180deg)' : 'none',
    }}
  >
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 7.5L10 12.5L15 7.5" stroke="#667085" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </Box>
);

/**
 * A single collapsible cleaning-task card: a header summarising status and schedule,
 * and an expandable body with the status/latest-update cards, the weekly schedule
 * editor, the "missed after" trigger, and the history list.
 */
export const CleaningTaskCard: React.FC<CleaningTaskCardProps> = ({
  t,
  isSmallDevice,
  title,
  status,
  lastUpdated,
  history,
  schedule,
  enabled,
  enabledLabel,
  statusLabel,
  lastUpdatedLabel,
  scheduleLabel,
  historyLabel,
  trigger,
  triggerLabel,
  readOnly = false,
  defaultExpanded = true,
  onScheduleChange,
  onTriggerChange,
  onEnabledChange,
}: CleaningTaskCardProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  // The threshold is held locally while it is being typed and only saved on blur or Enter — the
  // same moment the RJSF number widget saves. Saving per keystroke would write "1" then "15" for
  // a typed 15, and would write 0 the instant the field is cleared.
  const [triggerDraft, setTriggerDraft] = useState(String(trigger ?? 0));
  const [savedTrigger, setSavedTrigger] = useState(trigger);
  if (trigger !== savedTrigger) {
    setSavedTrigger(trigger);
    setTriggerDraft(String(trigger ?? 0));
  }

  const commitTrigger = (): void => {
    const parsed = Number(triggerDraft);
    // An empty or nonsensical box is not a value to save — put back what is stored.
    if (triggerDraft.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      setTriggerDraft(String(trigger ?? 0));
      return;
    }
    if (parsed !== (trigger ?? 0)) onTriggerChange(parsed);
  };

  const scheduledCount = getScheduledDayCount(schedule);
  const scheduleSummary = t('asset:cleaning_management:days_scheduled', {
    defaultValue: `${scheduledCount} ${scheduledCount === 1 ? 'day' : 'days'} scheduled`,
    count: scheduledCount,
  });

  return (
    <Box sx={{ border: '1px solid #EAECF0', borderRadius: '8px' }}>
      <Box
        component="button"
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '14px 16px',
          cursor: 'pointer',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          '&:hover': { backgroundColor: '#F9FAFB' },
        }}
      >
        <Chevron open={expanded} />
        <Typography sx={{ flex: 1, fontSize: '15px', fontWeight: 600, color: '#182230' }}>{title}</Typography>
        {status && <Chip label={status} color={getStatusChipColor(status)} size="small" />}
        <Typography sx={{ fontSize: '12px', color: '#667085' }}>{scheduleSummary}</Typography>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {enabledLabel && (
            <SwitchWithLabel
              id="cleaning-task-enabled"
              label={enabledLabel}
              checked={!!enabled}
              disabled={readOnly}
              onChange={(e) => onEnabledChange(e.target.checked)}
            />
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            <Box sx={summaryCardSx}>
              {statusLabel && <Typography sx={summaryLabelSx}>{statusLabel}</Typography>}
              <Box sx={{ display: 'flex' }}>
                {status ? (
                  <Chip label={status} color={getStatusChipColor(status)} size="small" />
                ) : (
                  <Typography sx={{ fontSize: '13px', color: '#98A2B3' }}>—</Typography>
                )}
              </Box>
            </Box>
            <Box sx={summaryCardSx}>
              {lastUpdatedLabel && <Typography sx={summaryLabelSx}>{lastUpdatedLabel}</Typography>}
              <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#182230' }}>
                {formatLastUpdated(lastUpdated) || '—'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {scheduleLabel && <Typography sx={sectionHeadingSx}>{scheduleLabel}</Typography>}
            <CleaningScheduleGrid
              t={t}
              isSmallDevice={isSmallDevice}
              value={schedule}
              onChange={onScheduleChange}
              disabled={readOnly}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                borderTop: '1px solid #F2F4F7',
                paddingTop: '10px',
              }}
            >
              {triggerLabel && <Typography sx={{ fontSize: '13px', color: '#475467' }}>{triggerLabel}</Typography>}
              <Box sx={{ width: '80px' }}>
                <TextField
                  size="small"
                  type="number"
                  fullWidth
                  disabled={readOnly}
                  value={triggerDraft}
                  // The label sits beside the field rather than above it, so the input carries it
                  // as an aria-label — otherwise a screen reader reads an unnamed number box.
                  inputProps={{ min: 0, 'aria-label': triggerLabel }}
                  onChange={(e) => setTriggerDraft(e.target.value)}
                  onBlur={commitTrigger}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitTrigger();
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              borderTop: '1px solid #EAECF0',
              paddingTop: '14px',
            }}
          >
            {historyLabel && <Typography sx={sectionHeadingSx}>{historyLabel}</Typography>}
            <CleaningHistoryList t={t} history={history} />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};
